import React, { useEffect } from 'react';
import { AppContexts } from '~/contexts';
import { useConfig } from '~/core/configuration/configContext';
import { useSearchState } from '~/core/useSearchState';
import { useDebug } from '~/development/debugging';
import useGoToResult from '~/hooks/useGoToResult';
import useShowSearch from '~/hooks/useShowSearch';
import { InitialErrorBoundary } from './components/errors/InitialErrorBoundary';
import { useLogoClickHandler } from './hooks/useLogoClickHandler';
import { useDynamicComponents } from './plugins/dynamicComponents/dynamicComponentsContext';

/**
 * Top level component of the application.
 * Provides state information and actions to the rest of the application.
 * Provides contexts and error boundaries.
 */
export const App = () => {
    const { Main } = useDynamicComponents();

    const innerSERPRef = React.useRef<HTMLDivElement>(null);
    const productsTitleRef = React.useRef<HTMLDivElement>(null);
    const isDebugMode = useDebug();
    const config = useConfig();

    const {
        state,
        resetState,
        updateSearchQuery,
        loadMore,
        updateIndexFilterControls,
        updateFilterControls,
        resetFilterControls,
        cancelFilter,
        cancelAllFilters,
        sortResults,
        setInstantSearch,
        setResultLayout,
        setSelectedTab,
    } = useSearchState({
        config,
        isDebugMode,
        productsTitleRef,
    });
    const { hideBodyOverflow, isOpen, closeSERP } = useShowSearch({
        searchQuery: state.searchQuery,
        updateSearchQuery,
        searchElementSelector: config.nativeSearchId,
        innerSERPRef,
        resultsCount: state.results.totalRecordsCount,
        loadingFinished: state.loadingFinished,
        resetState,
        searchTriggerSelector: config.layout.configuration.instantSearch?.nativeSearchElements?.trigger,
        searchContainerSelector: config.layout.configuration.instantSearch?.nativeSearchElements?.inputContainer,
        instantSearchEnabled: config.layout.configuration.instantSearch?.useInstantSearch,
        isInstantSearch: state.isInstantSearch,
    });
    const { handleGoToResult } = useGoToResult({
        searchQuery: state.searchQuery,
        resultsCount: state.results.filteredCategoriesRecordsCount,
        isInstantSearch: state.isInstantSearch,
    });
    const logoClickProperties = useLogoClickHandler({
        searchQuery: state.searchQuery,
        resultsCount: state.results.totalRecordsCount,
        loadingFinished: state.loadingFinished,
        logoLink: config.layout.configuration.logoLink,
    });

    useEffect(() => {
        if (isDebugMode) {
            console.log('SERP NEW STATE:', state);
        }
    }, [isDebugMode, state]);

    if (!isOpen) {
        return null;
    }

    return (
        <InitialErrorBoundary>
            <AppContexts
                updateSearchQuery={updateSearchQuery}
                state={state}
                actions={{
                    updateFilterControls,
                    resetFilterControls,
                    updateIndexFilterControls,
                    loadMore,
                    cancelFilter,
                    cancelAllFilters,
                    handleGoToResult,
                    sortResults,
                    setResultLayout,
                    setSelectedTab,
                }}
            >
                <Main
                    innerSERPRef={innerSERPRef}
                    closeSERP={closeSERP}
                    logoClickProperties={logoClickProperties}
                    productsTitleRef={productsTitleRef}
                    isInstantSearch={state.isInstantSearch}
                    setInstantSearch={setInstantSearch}
                    hideBodyOverflow={hideBodyOverflow}
                />
            </AppContexts>
        </InitialErrorBoundary>
    );
};
